/**
 * Workflows — Overview tab.
 *
 * Lands at /portal/workflows (the index under the workflows layout). Shows an
 * at-a-glance stats row (the things an operator acts on — DLQ, exhausted
 * retries, executions paused on approval, and live runs) above the tile
 * dashboard linking to every workflows surface.
 *
 * All four stats come from existing endpoints and are fetched best-effort, so a
 * hiccup in one never blanks the page.
 */

import { useCallback, useEffect, useState } from "react";

import { createFileRoute, Link } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import {
  ActivityIcon,
  AlertOctagonIcon,
  type LucideIcon,
  FileJsonIcon,
  FolderTreeIcon,
  HistoryIcon,
  LayoutGridIcon,
  PauseCircleIcon,
  PlayIcon,
  RepeatIcon,
  WorkflowIcon,
} from "lucide-react";

import {
  ActionCard,
  ActionCardGrid,
  ActionCardSection,
} from "@/components/ui/action-card";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PageHeader } from "@/components/ui/page-header";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { retryFailuresApi, workflowsApi } from "@/services/api/workflows";

export const Route = createFileRoute("/portal/workflows/")({
  component: WorkflowsOverview,
});

const authoringFeatures = [
  {
    title: "Definitions",
    description:
      "Author + version your workflows here. Drag-from-palette canvas; the Triggers tab on each definition manages how it fires.",
    icon: FileJsonIcon,
    href: "/portal/workflows/definitions",
    iconColor: "bg-amber-500",
  },
  {
    title: "Projects",
    description:
      "Group related workflows into one pipeline. Set the entry point, order the steps, and run the whole project on demand.",
    icon: FolderTreeIcon,
    href: "/portal/workflows/projects",
    iconColor: "bg-violet-500",
  },
];

const operationsFeatures = [
  {
    title: "Executions",
    description:
      "Recent workflow runs with status chips. Spot failures, drill into errors, audit what ran.",
    icon: HistoryIcon,
    href: "/portal/workflows/executions",
    iconColor: "bg-indigo-500",
  },
  {
    title: "Topology",
    description:
      "Streams + producer health, current XLEN depth, active subscription count per stream.",
    icon: ActivityIcon,
    href: "/portal/workflows/topology",
    iconColor: "bg-teal-500",
  },
  {
    title: "DLQ",
    description:
      "Failed events across producers. Inspect payloads + retry / discard rows from the admin queue.",
    icon: AlertOctagonIcon,
    href: "/portal/workflows/dlq",
    iconColor: "bg-rose-500",
  },
  {
    title: "Retry failures",
    description:
      "Node retries that exhausted their budget. Triage, resolve with a note. Admins only.",
    icon: RepeatIcon,
    href: "/portal/workflows/retry-failures",
    iconColor: "bg-orange-500",
  },
];

const devFeatures = [
  {
    title: "Execute (dev)",
    description:
      "Paste raw workflow JSON and run it. Developer surface for one-shot smokes + debugging — not for production authoring.",
    icon: PlayIcon,
    href: "/portal/workflows/execute",
    iconColor: "bg-slate-500",
  },
  {
    title: "Canvas demo (dev)",
    description:
      "Standalone canvas playground without the definition plumbing. Sandbox for the editor itself.",
    icon: LayoutGridIcon,
    href: "/portal/workflows/canvas-demo",
    iconColor: "bg-slate-500",
  },
];

interface Stats {
  // null = that source failed to load (or isn't permitted) → card shows "—",
  // never a misleading "0".
  dlq: number | null;
  retries: number | null;
  suspended: number | null;
  running: number | null;
}

interface StatCardProps {
  label: string;
  value: number | null;
  sub: string;
  icon: LucideIcon;
  /** Tailwind text colour for the value + sub — applied only when value > 0. */
  alertColor: string;
  href: string;
}

function StatCard({ label, value, sub, icon: Icon, alertColor, href }: StatCardProps) {
  const active = (value ?? 0) > 0;
  const tone = active ? alertColor : "text-foreground";
  return (
    <Link to={href} className="block">
      <Card className="transition-shadow hover:shadow-md">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {label}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className={`text-3xl font-bold ${tone}`}>
            {value === null ? "—" : value}
          </div>
          <div
            className={`mt-1 flex items-center gap-1 text-xs ${active ? alertColor : "text-muted-foreground"}`}
          >
            <Icon className="h-3 w-3" />
            {sub}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function WorkflowsOverview() {
  const { token } = useAuth();
  const { bypassesGroups } = usePermissions();
  const { selectedOrganisation } = usePortalData();
  const organisationId = selectedOrganisation?.organisationId ?? "";
  const [stats, setStats] = useState<Stats | null>(null);

  const loadStats = useCallback(async () => {
    // Each stat is best-effort — a failure (or no permission) keeps that field
    // null so its card shows "—", never a misleading "0". The page + the other
    // cards still render.
    const [dlq, execs, retries] = await Promise.all([
      workflowsApi
        .listDlq(token ?? undefined)
        .then((r) => r.total)
        .catch(() => null),
      // Only the two figures the cards show — counting just these statuses is
      // two index seeks, not a pass over the org's whole history.
      workflowsApi
        .countExecutions(token ?? undefined, {
          statuses: ["running", "suspended"],
        })
        .then((r) => r.counts_by_status)
        .catch(() => null),
      // Retry-failures is an admin/group-bypass endpoint (it 403s for members,
      // same gate as the Retries page). Don't fire a doomed call — show "—".
      organisationId && bypassesGroups
        ? retryFailuresApi
            .listUnresolved(organisationId, token ?? undefined)
            .then((rows) => rows.length)
            .catch(() => null)
        : Promise.resolve(null),
    ]);
    setStats({
      dlq,
      retries,
      suspended: execs ? (execs.suspended ?? 0) : null,
      running: execs ? (execs.running ?? 0) : null,
    });
  }, [token, organisationId, bypassesGroups]);

  useEffect(() => {
    void loadStats();
  }, [loadStats]);

  return (
    <>
      <Helmet>
        <title>Workflows | Portal</title>
      </Helmet>

      <div className="space-y-6">
        <PageHeader
          title="Workflows"
          description="Event-driven workflow orchestration — subscribe, author, execute, observe."
          icon={WorkflowIcon}
          eyebrow="Automation"
          gradient
        />

        {/* At-a-glance: everything here is something an operator acts on. */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label="DLQ"
            value={stats ? stats.dlq : null}
            sub="dead-lettered events"
            icon={AlertOctagonIcon}
            alertColor="text-rose-600"
            href="/portal/workflows/dlq"
          />
          <StatCard
            label="Retries"
            value={stats ? stats.retries : null}
            sub="exhausted — awaiting resolution"
            icon={RepeatIcon}
            alertColor="text-rose-600"
            href="/portal/workflows/retry-failures"
          />
          <StatCard
            label="Approvals pending"
            value={stats ? stats.suspended : null}
            sub="paused on human approval"
            icon={PauseCircleIcon}
            alertColor="text-amber-600"
            href="/portal/workflows/executions"
          />
          <StatCard
            label="Running now"
            value={stats ? stats.running : null}
            sub="executing"
            icon={ActivityIcon}
            alertColor="text-blue-600"
            href="/portal/workflows/executions"
          />
        </div>

        <ActionCardSection title="Authoring">
          <ActionCardGrid columns={2}>
            {authoringFeatures.map((f) => (
              <ActionCard key={f.href} {...f} />
            ))}
          </ActionCardGrid>
        </ActionCardSection>

        <ActionCardSection title="Operations">
          <ActionCardGrid columns={2}>
            {operationsFeatures.map((f) => (
              <ActionCard key={f.href} {...f} />
            ))}
          </ActionCardGrid>
        </ActionCardSection>

        <ActionCardSection title="Developer tools">
          <ActionCardGrid columns={2}>
            {devFeatures.map((f) => (
              <ActionCard key={f.href} {...f} />
            ))}
          </ActionCardGrid>
        </ActionCardSection>
      </div>
    </>
  );
}
